import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { NetworkError, TechnicalError } from '../../../common/errors';
import Result from '../../../common/result';
import { Action, UserFlow } from '../../../config/networkConfig';
import RemoteModeSettable from '../../common/remoteModeSettable';
import { Token } from '../../common/token';
import HttpValidationService from '../httpValidationService';
import {
  CredentialsResult,
  DeleteValidationResult,
  GetStepRequest,
  ProcessStepRequest,
  Step,
  StepResult,
  UploadResult,
  UserStatusResult,
  Validation,
  ValidationRequest,
  ValidationResult,
  ValidationService,
  ValidationStatus,
} from '../validationService';
import CredentialReusable from '../../common/credentialReusable';

export default abstract class LocalOrRemoteValidationService
  implements ValidationService, RemoteModeSettable, CredentialReusable
{
  protected remoteMode: boolean;

  protected credentialReuseMode: boolean;

  protected id: string;

  protected status: ValidationStatus;

  protected token?: Token;

  constructor(
    private userFlow: UserFlow,
    protected action: Action,
  ) {
    this.remoteMode = false;
    this.credentialReuseMode = false;
    this.id = uuidv4();
    this.status = ValidationStatus.collecting;
  }

  setCredentialReuseMode(reuseMode: boolean): void {
    this.credentialReuseMode = reuseMode;
  }

  setRemoteMode(remoteMode: boolean): void {
    this.remoteMode = remoteMode;
  }

  protected abstract getLocalValidation(): Validation;

  protected abstract processStepLocally(request: ProcessStepRequest): Promise<StepResult>;

  async create(): Promise<ValidationResult> {
    return Result.successful(this.getLocalValidation());
  }

  async getNextStep(): Promise<StepResult> {
    return Result.failed(new TechnicalError('getNextStep not supported for LocalOrRemoteValidationService'));
  }

  private getServerProxy(): ValidationService {
    const httpValidationService = new HttpValidationService();
    httpValidationService.setNetworkContext(this.userFlow, this.action);
    if (this.token) {
      httpValidationService.setToken(this.token);
    }

    return httpValidationService;
  }

  async getStep(request: GetStepRequest): Promise<StepResult> {
    if (this.remoteMode) {
      return this.getServerProxy().getStep(request);
    }

    const step = R.find((localStep: Step) => localStep.name === request.step, this.getLocalValidation().steps);
    return step
      ? Result.successful(step)
      : Result.failed(new TechnicalError(`Invalid step ${request.step} passed to LocalOrRemoteValidationService`));
  }

  async processStep(request: ProcessStepRequest): Promise<StepResult> {
    if (this.remoteMode) {
      return this.getServerProxy().processStep(request);
    }

    return this.processStepLocally(request);
  }

  async getValidationResult(request: ValidationRequest): Promise<ValidationResult> {
    if (this.remoteMode) {
      return this.getServerProxy().getValidationResult(request);
    }

    return Result.successful(this.getLocalValidation());
  }

  async deleteValidation(): Promise<DeleteValidationResult> {
    return Result.noValueSuccess();
  }

  async getStatus(): Promise<UserStatusResult> {
    return Result.failed(new NetworkError('No status for LocalOrRemoteValidationService', 404));
  }

  async uploadFile(): Promise<UploadResult> {
    return Result.failed(new TechnicalError('uploadFile not supported for LocalOrRemoteValidationService'));
  }

  async getCredentials(): Promise<CredentialsResult> {
    return Result.failed(new TechnicalError('getCredentials not supported for UniquenessValidationService'));
  }

  setToken(token: Token): void {
    this.token = token;
  }
}
